import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import './course-sidebar-tabs.css';

function OnlineBadge({ count, label = 'en línea' }) {
    if (!count || count <= 0) return null;

    return (
        <span
            className="course-sidebar-tabs__badge course-sidebar-tabs__badge--online"
            aria-label={`${count} ${label}`}
        >
            <span className="course-sidebar-tabs__badge-dot" aria-hidden="true" />
            <span className="course-sidebar-tabs__badge-count">{count}</span>
        </span>
    );
}

function LiveBadge() {
    return (
        <span className="course-sidebar-tabs__badge course-sidebar-tabs__badge--live" aria-label="Clase en vivo">
            <span className="course-sidebar-tabs__badge-dot" aria-hidden="true" />
        </span>
    );
}

const SCROLL_STEP = 140;
const EDGE_EPS = 2;

export function CourseSidebarTabs({
    tabs,
    activeTab,
    onTabChange,
    onlineCount = 0,
    liveCount = 0,
    showLiveOnClasses = false,
}) {
    const trackRef = useRef(null);
    const scrollRef = useRef(null);
    const tabRefs = useRef({});
    const [indicator, setIndicator] = useState({ left: 0, width: 0 });
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollEdges = useCallback(() => {
        const el = scrollRef.current;
        if (!el) {
            setCanScrollLeft(false);
            setCanScrollRight(false);
            return;
        }
        const max = el.scrollWidth - el.clientWidth;
        const overflow = max > EDGE_EPS;
        setCanScrollLeft(overflow && el.scrollLeft > EDGE_EPS);
        setCanScrollRight(overflow && el.scrollLeft < max - EDGE_EPS);
    }, []);

    const updateIndicator = useCallback(() => {
        const track = trackRef.current;
        const activeEl = tabRefs.current[activeTab];
        if (!track || !activeEl) return;

        const trackRect = track.getBoundingClientRect();
        const tabRect = activeEl.getBoundingClientRect();

        setIndicator({
            left: tabRect.left - trackRect.left,
            width: tabRect.width,
        });
        updateScrollEdges();
    }, [activeTab, updateScrollEdges]);

    const scrollByAmount = useCallback((delta) => {
        const el = scrollRef.current;
        if (!el) return;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollBy({
            left: delta,
            behavior: reduceMotion ? 'auto' : 'smooth',
        });
    }, []);

    const scrollActiveTabIntoView = useCallback(() => {
        const scrollEl = scrollRef.current;
        const activeEl = tabRefs.current[activeTab];
        if (!scrollEl || !activeEl) return;

        const scrollRect = scrollEl.getBoundingClientRect();
        const tabRect = activeEl.getBoundingClientRect();
        const padding = 12;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (tabRect.left < scrollRect.left + padding) {
            scrollEl.scrollBy({
                left: tabRect.left - scrollRect.left - padding,
                behavior: reduceMotion ? 'auto' : 'smooth',
            });
        } else if (tabRect.right > scrollRect.right - padding) {
            scrollEl.scrollBy({
                left: tabRect.right - scrollRect.right + padding,
                behavior: reduceMotion ? 'auto' : 'smooth',
            });
        }
    }, [activeTab]);

    useLayoutEffect(() => {
        updateIndicator();
        scrollActiveTabIntoView();
    }, [updateIndicator, scrollActiveTabIntoView, tabs]);

    useEffect(() => {
        const track = trackRef.current;
        const scrollEl = scrollRef.current;
        if (!track || !scrollEl) return undefined;

        const onScroll = () => {
            updateIndicator();
            updateScrollEdges();
        };

        const observer = new ResizeObserver(() => {
            updateIndicator();
            updateScrollEdges();
        });
        observer.observe(track);
        observer.observe(scrollEl);
        window.addEventListener('resize', updateIndicator);
        scrollEl.addEventListener('scroll', onScroll, { passive: true });

        // Rueda vertical → scroll horizontal de tabs
        const onWheel = (event) => {
            const el = scrollRef.current;
            if (!el) return;
            const max = el.scrollWidth - el.clientWidth;
            if (max <= EDGE_EPS) return;

            const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX)
                ? event.deltaY
                : event.deltaX;
            if (!delta) return;

            const atStart = el.scrollLeft <= EDGE_EPS;
            const atEnd = el.scrollLeft >= max - EDGE_EPS;
            if ((delta < 0 && atStart) || (delta > 0 && atEnd)) return;

            event.preventDefault();
            el.scrollLeft += delta;
            updateScrollEdges();
        };

        scrollEl.addEventListener('wheel', onWheel, { passive: false });

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateIndicator);
            scrollEl.removeEventListener('scroll', onScroll);
            scrollEl.removeEventListener('wheel', onWheel);
        };
    }, [updateIndicator, updateScrollEdges]);

    return (
        <div className="course-sidebar-tabs" role="tablist" aria-label="Panel del curso">
            {canScrollLeft ? (
                <button
                    type="button"
                    className="course-sidebar-tabs__nav course-sidebar-tabs__nav--prev"
                    aria-label="Ver tabs anteriores"
                    onClick={() => scrollByAmount(-SCROLL_STEP)}
                >
                    <ChevronLeftIcon className="course-sidebar-tabs__nav-icon" aria-hidden="true" />
                </button>
            ) : null}

            <div className="course-sidebar-tabs__scroll" ref={scrollRef}>
                <div className="course-sidebar-tabs__track" ref={trackRef}>
                    <span
                        className="course-sidebar-tabs__indicator"
                        style={{
                            transform: `translateX(${indicator.left}px)`,
                            width: indicator.width,
                        }}
                        aria-hidden="true"
                    />
                    {tabs.map((tab) => {
                        const isActive = activeTab === tab.id;

                        return (
                            <button
                                key={tab.id}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                ref={(el) => {
                                    tabRefs.current[tab.id] = el;
                                }}
                                className={`course-sidebar-tabs__tab${isActive ? ' is-active' : ''}`}
                                onClick={() => onTabChange(tab.id)}
                            >
                                <span className="course-sidebar-tabs__label">{tab.label}</span>
                                {tab.id === 'classes' && showLiveOnClasses ? <LiveBadge /> : null}
                                {tab.id === 'participants' ? <OnlineBadge count={onlineCount} /> : null}
                                {tab.id === 'live' ? (
                                    <OnlineBadge count={liveCount} label="en la videollamada" />
                                ) : null}
                            </button>
                        );
                    })}
                </div>
            </div>

            {canScrollRight ? (
                <button
                    type="button"
                    className="course-sidebar-tabs__nav course-sidebar-tabs__nav--next"
                    aria-label="Ver más tabs"
                    onClick={() => scrollByAmount(SCROLL_STEP)}
                >
                    <ChevronRightIcon className="course-sidebar-tabs__nav-icon" aria-hidden="true" />
                </button>
            ) : null}
        </div>
    );
}
